/**
 * AllergyAlertWarning Component
 * 
 * Displays potential allergen warnings when adding a medication that may
 * contain ingredients the user is allergic to.
 * 
 * ⚠️ CRITICAL DISCLAIMER: This is for INFORMATIONAL PURPOSES ONLY.
 * Allergic reactions can be serious or life-threatening.
 * ALWAYS consult your doctor or pharmacist before taking any new medication.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertTriangle,
  AlertOctagon,
  Info,
  ShieldAlert,
  Phone,
  Stethoscope,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  Pill,
  HeartPulse,
} from 'lucide-react';
import {
  RISK_LEVEL,
  RISK_COLORS,
  RISK_LABELS,
  ALLERGY_DISCLAIMER,
  getHighestRiskLevel,
} from '@/services/allergyAlertService';

const AllergyAlertWarning = ({
  isOpen,
  onClose,
  alerts,
  medicationName,
  onProceed,
  onCancel,
}) => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  
  const highestRisk = getHighestRiskLevel(alerts);
  const hasHighRisk = alerts?.some(a => a.risk_level === RISK_LEVEL.HIGH);
  const hasModerateRisk = alerts?.some(a => a.risk_level === RISK_LEVEL.MODERATE);

  // Get icon based on risk level
  const getRiskIcon = (riskLevel) => {
    switch (riskLevel) {
      case RISK_LEVEL.HIGH:
        return <AlertOctagon className="h-5 w-5" />;
      case RISK_LEVEL.MODERATE:
        return <AlertTriangle className="h-5 w-5" />;
      case RISK_LEVEL.LOW:
        return <Info className="h-5 w-5" />;
      default:
        return <CheckCircle2 className="h-5 w-5" />;
    }
  };

  const getHeaderColors = () => {
    if (hasHighRisk) return 'bg-red-600';
    if (hasModerateRisk) return 'bg-amber-500';
    return 'bg-yellow-500';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0">
        {/* Header with warning color */}
        <div className={`${getHeaderColors()} text-white p-4`}>
          <DialogHeader className="space-y-2">
            <DialogTitle className="flex items-center gap-2 text-white">
              <ShieldAlert className="h-6 w-6" />
              Potential Allergen{alerts?.length > 1 ? 's' : ''} Detected
            </DialogTitle>
            <DialogDescription className="text-white/90">
              This medication may contain ingredients you&apos;re allergic to
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* CRITICAL Disclaimer Banner */}
          <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg">
            <div className="flex items-start gap-3">
              <HeartPulse className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-bold text-red-900 mb-1">
                  ⚠️ Not Medical Advice - Consult Your Doctor
                </h4>
                <p className="text-sm text-red-800">
                  This is for <strong>informational purposes only</strong>. Allergic reactions 
                  can be <strong>serious or life-threatening</strong>. <strong>ALWAYS</strong> consult 
                  your <strong>doctor or pharmacist</strong> before taking any new medication.
                </p>
              </div>
            </div>
          </div>

          {/* Medication Being Added */}
          <div className="p-3 bg-slate-100 rounded-lg flex items-center gap-3">
            <Pill className="h-5 w-5 text-slate-500" />
            <div>
              <span className="text-sm text-slate-600">Adding medication:</span>
              <p className="font-semibold text-slate-900">{medicationName}</p>
            </div>
          </div>

          {/* Alert List */}
          <div className="space-y-3">
            <h4 className="font-medium text-slate-700 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Potential Allergen Concerns ({alerts?.length || 0})
            </h4>
            
            {alerts?.map((alert, index) => {
              const colors = RISK_COLORS[alert.risk_level] || RISK_COLORS[RISK_LEVEL.LOW];
              const isExpanded = expandedIndex === index;
              
              return (
                <div
                  key={index}
                  className={`rounded-lg border-2 ${colors.border} overflow-hidden`}
                >
                  {/* Alert Header */}
                  <button
                    onClick={() => setExpandedIndex(isExpanded ? null : index)}
                    className={`w-full p-3 ${colors.bg} flex items-center justify-between text-left`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={colors.icon}>
                        {getRiskIcon(alert.risk_level)}
                      </span>
                      <div>
                        <span className={`font-medium ${colors.text}`}>
                          {RISK_LABELS[alert.risk_level] || 'Potential Concern'}
                        </span>
                        <p className="text-sm text-slate-600">
                          Your allergy: <strong>{alert.allergen}</strong>
                          {alert.cross_reactivity && (
                            <span className="ml-2 text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded">
                              Cross-reactivity
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-slate-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-500" />
                    )}
                  </button>
                  
                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="p-3 bg-white border-t space-y-3">
                      {alert.medication_ingredient && (
                        <div>
                          <span className="text-xs font-medium text-slate-500 uppercase">
                            Ingredient of Concern
                          </span>
                          <p className="text-sm text-slate-700 font-medium">
                            {alert.medication_ingredient}
                          </p>
                        </div>
                      )}
                      
                      {alert.explanation && (
                        <div>
                          <span className="text-xs font-medium text-slate-500 uppercase">
                            Why This May Be a Concern
                          </span>
                          <p className="text-sm text-slate-700">{alert.explanation}</p>
                        </div>
                      )}
                      
                      {alert.recommendation && (
                        <div className="p-2 bg-blue-50 rounded text-sm text-blue-800 border border-blue-200">
                          <strong>💬 What to Ask Your Doctor/Pharmacist:</strong>
                          <p className="mt-1">{alert.recommendation}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Consult Healthcare Provider - EMPHASIZED */}
          <div className="p-4 bg-emerald-50 border-2 border-emerald-300 rounded-lg">
            <div className="flex items-start gap-3">
              <Phone className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-bold text-emerald-900 mb-1">
                  📞 Talk to Your Doctor or Pharmacist
                </h4>
                <p className="text-sm text-emerald-800 mb-2">
                  {ALLERGY_DISCLAIMER.action}
                </p>
                <ul className="text-xs text-emerald-700 space-y-1 list-disc list-inside">
                  <li>Show them the actual medication label</li>
                  <li>Discuss your allergy history</li>
                  <li>Ask about alternative medications if needed</li>
                  <li>Get their professional assessment</li>
                </ul>
              </div>
            </div>
          </div>

          {/* When in Doubt Banner */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-center">
            <p className="text-sm font-semibold text-amber-800">
              🚨 {ALLERGY_DISCLAIMER.urgent}
            </p>
          </div>

          {/* Full Disclaimer */}
          <div className="p-3 bg-slate-50 rounded-lg text-xs text-slate-600 border">
            <p className="font-medium mb-1">Disclaimer:</p>
            <p>{ALLERGY_DISCLAIMER.full}</p>
          </div>
        </div>

        {/* Footer Actions */}
        <DialogFooter className="p-4 border-t bg-slate-50 flex-shrink-0">
          <div className="w-full space-y-2">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  onCancel?.();
                  onClose();
                }}
                className="flex-1 border-2"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Don&apos;t Add Medication
              </Button>
              <Button
                onClick={() => {
                  onProceed?.();
                  onClose();
                }}
                className={`flex-1 ${
                  hasHighRisk 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : hasModerateRisk 
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                I&apos;ll Consult My Doctor
              </Button>
            </div>
            <p className="text-xs text-center text-slate-500">
              By continuing, you acknowledge this is <strong>not medical advice</strong> and 
              you will <strong>consult your healthcare provider</strong>
            </p>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AllergyAlertWarning;
